import {AxiosResponse} from 'axios';
import React, {FC} from 'react';
import {useSelector} from 'react-redux';

import {
    pickDataFromQueryByAdditionalFields,
    shouldPickDataFromQueryByAdditionalInfoKeys,
} from 'components/table/action/table-action.utils';
import {EMetaActionType, selectMetadata} from 'entities/metadata';
import {selectContextRawData} from 'modules/context/context-selector';
import {selectEntityData} from 'modules/data';
import {
    actionCreateListCP,
    actionSendApiRequest,
    actionsRequestButton,
    createActionConfirm,
    createActionGetFile,
    createActionLastPeriod,
    resetData,
} from 'modules/data/data-actions';
import {ITableEntityData} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {useRequiredQueryParams} from 'shared/hooks/use-required-query-params';
import {ERequestType} from 'shared/types/requests';
import {showMessage, showMessageFromResponse} from 'shared/utils';
import {useQueryParams} from 'shared/utils/query-params';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {TAppState} from 'store/config/types';
import {selectUserInfo} from 'store/slices/auth-slice';
import {tableReportSliceActions} from 'store/slices/table-report-slice';
import {normalizeEntityName} from 'utils/common';

import {ActionButton} from '../action-button';
import {ITableActionProps} from '../table-action-types';

interface INotificationWithLink {
    message: string;
    hasViewDataLink: boolean;
}

const ButtonsActionComponent: FC<ITableActionProps> = ({
    wrapper,
    meta,
    entityName,
    ...props
}: ITableActionProps) => {
    const dispatch = useAppDispatch();
    const contextData = useAppSelector(selectContextRawData);

    const {selectedRowKeys = []} = useSelector((state: TAppState) => (
        selectEntityData(entityName,
            EEntityType.TABLE)(state) || {}) as ITableEntityData);
    const {userName} = useAppSelector(selectUserInfo) || {};
    const filterMeta = useAppSelector(selectMetadata(entityName, EEntityType.FILTER));
    const {getQueryParam} = useQueryParams();
    const {
        setModalReportDownloadDocuments,
    } = tableReportSliceActions;

    const {
        actionType,
        requestType,
        referenceUrl,
        additionalInfoKeys,
        actionIcon,
        actionTitle,
        requiredQueryParams,
        shouldReloadTable = false,
    } = meta;

    const {isDisabled} = useRequiredQueryParams({requiredQueryParams});

    const handleClick = async () => {
        if (actionType === EMetaActionType.CREATE_LIST_CP) {
            dispatch(
                actionCreateListCP(entityName, EEntityType.TABLE, referenceUrl, requestType, contextData),
            );
        } else if (
            actionType === EMetaActionType.UNLOAD_CP
            || actionType === EMetaActionType.DOWNLOAD_TEMPLATE
            || actionType === EMetaActionType.DOWNLOAD_ALL_EXCEL
            || actionType === EMetaActionType.DOWNLOAD_REPORT
            || actionType === EMetaActionType.DOWNLOAD_EXCEL
            || actionType === EMetaActionType.DOWNLOAD_XML
        ) {
            let queryParamsData = contextData;

            if (additionalInfoKeys
                    && shouldPickDataFromQueryByAdditionalInfoKeys({filterMeta})
                    && requestType === ERequestType.GET) {
                const additionalData = pickDataFromQueryByAdditionalFields({additionalInfoKeys, getQueryParam});
                queryParamsData = {
                    ...queryParamsData,
                    ...(additionalData ?? {}),
                };
            }

            dispatch(
                createActionGetFile({
                    referenceUrl,
                    requestType,
                    data: queryParamsData,
                }),
            );
        } else if (actionType === EMetaActionType.SEND_REMINDER) {
            dispatch(
                actionsRequestButton(referenceUrl, requestType, contextData),
            );
        } else if (actionType === EMetaActionType.BUTTON_CONFIRM) {
            dispatch(
                createActionConfirm(entityName, referenceUrl, requestType, contextData),
            );
        } else if (actionType === EMetaActionType.BUTTON_COPY_RULES_FROM_LAST_PERIOD) {
            dispatch(
                createActionLastPeriod(referenceUrl, requestType, contextData),
            );
        } else if (actionType === EMetaActionType.SEND_REQUEST_WITH_USER) {
            dispatch(actionsRequestButton(referenceUrl, requestType, contextData, {userName, ids: selectedRowKeys}));
        } else if (actionType === EMetaActionType.API_REQUEST_BUTTON) {
            const queryParamsData = (
                    additionalInfoKeys
                    && shouldPickDataFromQueryByAdditionalInfoKeys({filterMeta})
                    && requestType === ERequestType.POST)
                    ? pickDataFromQueryByAdditionalFields({additionalInfoKeys, getQueryParam})
                    : {};

            try {
              const response: AxiosResponse<INotificationWithLink> = await dispatch(actionSendApiRequest({
                referenceUrl,
                requestType,
                data: queryParamsData,
              }));

              const {message, hasViewDataLink} = response.data;

              if (hasViewDataLink) {
                  showMessage({
                      message: `${message}\n`,
                      modalData: {
                          text: 'Посмотреть результат\n',
                          onClick: () => {
                              dispatch(setModalReportDownloadDocuments({isOpen: true, wereDocumentsRead: true}));
                          },
                      },
                  });
              } else {
                  showMessageFromResponse({customMessage: message});
              }

              if (shouldReloadTable) {
                dispatch(resetData({
                  entityType: meta?.parentEntityType || EEntityType.TABLE,
                  entityName: normalizeEntityName(entityName),
                  loading: false,
                }));
              }
            } catch (e) {
                showMessageFromResponse({ response: e?.response, isError: true });
            }
        }
    };
    const Component = wrapper || ActionButton;
    return (
        <Component
            {...props}
            onClick={handleClick}
            actionIcon={actionIcon}
            disabled={isDisabled}
        >
            {actionTitle}
        </Component>
    );
};

export const ButtonsAction = ButtonsActionComponent;
